import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckIcon, StarIcon } from "../assets/icons/icons";

const FEATURE_SUMMARY_LINES = new Set(["All Free features", "All Free and Pro features"]);

const PRICING_TIERS = [
  {
    id: "free",
    name: "Free",
    description: "Start verifying your first license and show trust instantly.",
    pricing: { monthly: "0", annual: "0" },
    cta: "Start Free",
    accent: "#4f9fe0",
    accentSoft: "rgba(79, 159, 224, 0.18)",
    features: [
      "One verified license",
      "ClearBadge Verified directory listing",
      "Basic profile analytics",
      "Digital business card with contact share functionality",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    description: "Start with a 7-day free trial, then grow credibility, leads, and live trust verification with full control.",
    pricing: { monthly: "29.99", annual: "270" },
    standardPricing: { monthly: "39.99", annual: "360" },
    pricingNote: "7-day free trial. Cancel anytime. Promo pricing after trial.",
    cta: "Unlock Full Verification",
    accent: "#1b87d6",
    borderAccent: "#1b87d6",
    accentSoft: "rgba(27, 135, 214, 0.2)",
    highlight: true,
    badge: "Most Popular",
    features: [
      "All Free features",
      "Unlimited verified licenses",
      "Enhanced digital business card customization",
      "Detailed profile analytics",
      "Profile inquiry lead funnel",
      "Live Trust Session (camera + optional screen share, up to 1hr @ 720p)",
      "License renewal notices",
      "Custom email signature",
    ],
  },
  {
    id: "elite",
    name: "Elite",
    description: "Start with a 7-day free trial, then maximize visibility with premium placement and analytics.",
    pricing: { monthly: "49.99", annual: "450" },
    standardPricing: { monthly: "99.99", annual: "960" },
    pricingNote: "7-day free trial. Cancel anytime. Promo pricing after trial.",
    cta: "Claim Premium Visibility",
    accent: "#d2a23f",
    borderAccent: "#d2a23f",
    accentSoft: "rgba(210, 162, 63, 0.22)",
    features: [
      "All Free and Pro features",
      "Live Trust Session (up to 2hr @ 1080p)",
      "Premium visibility (gold border, sponsored tag, top-of-category listing)",
      "Premium analytics dashboard",
      "Secure link generation for verified professionals",
      "Custom digital business card vanity URL",
      "Priority support and early access to new tools",
    ],
  },
];

export default function PricingSection({
  id = "pricing",
  header = null,
  className = "",
  disableFreeCta = false,
  freeCtaLabel,
  currentTier,
  currentStatus,
  showBillingToggle = true,
  tierIds = null,
  tierOverrides = {},
} = {}) {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const isAnnual = billingCycle === "annual";
  const hasCurrentPlan =
    (currentTier !== undefined && currentTier !== null) ||
    (currentStatus !== undefined && currentStatus !== null);

  const resolvedCurrentTier = useMemo(() => {
    if (!hasCurrentPlan) return null;
    const tier = String(currentTier || "").toLowerCase();
    const status = String(currentStatus || "").toLowerCase();
    if (tier === "pro" || tier === "elite" || tier === "free") return tier;
    if (status === "pro" || status === "elite") return status;
    if (status === "active" || status === "trialing") return "pro";
    return "free";
  }, [currentStatus, currentTier, hasCurrentPlan]);

  const pricingCopy = useMemo(() => {
    return isAnnual ? { label: "/yr", detail: "Promo pricing" } : { label: "/mo", detail: "Promo pricing" };
  }, [isAnnual]);

  const tiers = useMemo(() => {
    if (!Array.isArray(tierIds) || tierIds.length === 0) return PRICING_TIERS;
    const wanted = new Set(tierIds.map((id) => String(id).toLowerCase()));
    return PRICING_TIERS.filter((tier) => wanted.has(String(tier.id).toLowerCase()));
  }, [tierIds]);

  const resolvedTiers = useMemo(() => {
    return tiers.map((tier) => ({
      ...tier,
      ...(tierOverrides?.[tier.id] || {}),
    }));
  }, [tierOverrides, tiers]);

  return (
    <section className={["pricing-section", className].filter(Boolean).join(" ")} id={id}>
      <div className="landing-container">
        {header || (
          <div className="section-head pricing-head">
            <p className="eyebrow pricing-eyebrow">Pricing</p>
            <h2>Simple & Transparent Pricing</h2>
            <p>No Surprise Fees.</p>
          </div>
        )}
        {header ? <h2 className="sr-only">Pricing plans</h2> : null}

        {showBillingToggle && (
          <div className="pricing-toggle" role="group" aria-label="Billing frequency" data-reveal style={{ "--reveal-delay": "1740ms" }}>
            <button
              type="button"
              className={billingCycle === "monthly" ? "active" : ""}
              aria-pressed={billingCycle === "monthly"}
              onClick={() => setBillingCycle("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={billingCycle === "annual" ? "active" : ""}
              aria-pressed={billingCycle === "annual"}
              onClick={() => setBillingCycle("annual")}
            >
              Annual <span className="toggle-badge">Save 25%</span>
            </button>
          </div>
        )}

        <div className="pricing-grid" data-reveal style={{ "--reveal-delay": "1880ms" }}>
          {resolvedTiers.map((tier, index) => {
            const amount = tier.pricing[billingCycle] || tier.pricing.monthly;
            const standardAmount = tier.standardPricing?.[billingCycle];
            const tierPricingDetail = tier.id === "free" ? "" : tier.pricingNote || pricingCopy.detail;
            const isCurrentTier = resolvedCurrentTier && tier.id === resolvedCurrentTier;
            const isIncludedFree =
              resolvedCurrentTier && resolvedCurrentTier !== "free" && tier.id === "free";
            const isIncludedPro =
              resolvedCurrentTier === "elite" && tier.id === "pro";
            const isFreeDisabled = disableFreeCta && tier.id === "free";
            const isCtaDisabled = isFreeDisabled || isCurrentTier || isIncludedFree || isIncludedPro;
            let ctaLabel = tier.id === "free" && freeCtaLabel ? freeCtaLabel : tier.cta;
            if (isIncludedFree) {
              ctaLabel = "Included in Your Plan";
            }
            if (isIncludedPro) {
              ctaLabel = "Included in Your Plan";
            }
            if (isCurrentTier) {
              ctaLabel = "Current";
            }
            const signupLink =
              tier.id === "free"
                ? "/login?mode=signup"
                : `/login?mode=signup&plan=${tier.id}&interval=${billingCycle}`;
            return (
              <article
                key={tier.id}
                className={`pricing-card ${tier.highlight ? "featured" : ""}`}
                data-tier={tier.id}
                data-reveal
                style={{
                  "--tier-accent": tier.accent,
                  "--tier-border": tier.borderAccent || tier.accent,
                  "--tier-accent-soft": tier.accentSoft,
                  animationDelay: `${index * 0.08}s`,
                  "--reveal-delay": `${2020 + index * 120}ms`,
                }}
              >
                {tier.badge && (
                  <div className="pricing-chip">
                    <span className="pricing-chip-star" aria-hidden="true">
                      <StarIcon className="pricing-star-icon" />
                    </span>
                    <span>{tier.badge}</span>
                  </div>
                )}
                <div className="pricing-card-head">
                  <h3>{tier.name}</h3>
                  <p className="muted">{tier.description}</p>
                </div>
                <div className="pricing-price">
                  <span className="pricing-currency">$</span>
                  <span className="pricing-amount">{amount}</span>
                  <span className="pricing-cycle">{pricingCopy.label}</span>
                  {standardAmount && standardAmount !== amount && (
                    <span className="pricing-strike">${standardAmount}</span>
                  )}
                </div>
                {tierPricingDetail ? <p className="pricing-note">{tierPricingDetail}</p> : null}
                {isCtaDisabled ? (
                  <button
                    className={`pricing-cta ${tier.highlight ? "primary" : "ghost"} disabled ${isIncludedPro ? "included-pro" : ""}`}
                    type="button"
                    disabled
                  >
                    {ctaLabel}
                  </button>
                ) : (
                  <Link className={`pricing-cta ${tier.highlight ? "primary" : "ghost"}`} to={signupLink}>
                    {ctaLabel}
                  </Link>
                )}
                <ul className="pricing-features">
                  {tier.features.map((feature) => (
                    <li
                      key={feature}
                      className={`pricing-feature${FEATURE_SUMMARY_LINES.has(feature) ? " pricing-feature-summary" : ""}`}
                    >
                      {FEATURE_SUMMARY_LINES.has(feature) ? (
                        <strong>{feature}</strong>
                      ) : (
                        <>
                          <CheckIcon className="pricing-check" />
                          <span>{feature}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
